import { useMemo } from 'react';

import {
  useGetOrganizationSettingsFullQuery,
  useGetOrgByIdQuery,
  useListOrganizationDepartmentsQuery
} from '@/store/slices/api/organizationApiSlice';
import {
  useGetPatientDiagnosesInfiniteQuery,
  useGetPatientInsurancesQuery,
  useGetPatientQuery
} from '@/store/slices/api/patientsApiSlice';
import {
  accountBillingType,
  accountEhrIntegrated,
  accountMultipleDiagnoses,
  buildEnrollmentChecklistInputs,
  diagnosisFactsFromPages,
  enrollmentSettingsFrom,
  evaluateEnrollmentChecklist,
  hfSchedulingFrom,
  isToggleOn,
  pickEnrollmentDevice,
  pickInactiveEnrollmentDevice,
  resolveEnrollmentSettings,
  resolveHfMonitoring
} from '@/utils/enrollmentChecklist';

import { sortPolicies } from '@/types/insurance';
import { DeviceVendor, DeviceVendorDetailsMap } from '@/types/matchDevice';

import type { EnrollmentChecklistResult } from '@/utils/enrollmentChecklist';

export interface PatientEnrollmentStatus {
  loading: boolean;
  error: boolean;
  evaluation: EnrollmentChecklistResult | null;
}

function vendorName(vendor: string): string {
  return DeviceVendorDetailsMap[vendor as DeviceVendor]?.name ?? vendor;
}

/**
 * Read-only CPE checklist status for one patient (patient list "Setup" column). Uses the same
 * queries and arguments as the CPE task page so the cache is shared. No enrollment record and
 * no in-place overrides. Settings/full is 403 for external users: that counts as "no settings".
 */
export function usePatientEnrollmentStatus(
  patientId: string,
  enabled: boolean
): PatientEnrollmentStatus {
  const skip = !enabled || !patientId;

  const patientQuery = useGetPatientQuery(patientId, { skip });
  const patient = patientQuery.data;
  const clinicId = patient?.clinic?.id || '';
  const clinicSkip = skip || !clinicId;

  const clinicQuery = useGetOrgByIdQuery(clinicId, { skip: clinicSkip });
  // The patient payload already includes the clinic. A failed org lookup must not blank the row.
  const clinic =
    clinicQuery.data?.organization ?? (clinicQuery.isError ? patient?.clinic : undefined);
  const parentId = clinic?.parent?.id || clinic?.parentId || '';
  const accountSkip = clinicSkip || !parentId || parentId === clinicId;
  const clinicSettings = useGetOrganizationSettingsFullQuery(clinicId, { skip: clinicSkip });
  const accountSettings = useGetOrganizationSettingsFullQuery(parentId, { skip: accountSkip });
  const departmentQuery = useListOrganizationDepartmentsQuery(clinicId, { skip: clinicSkip });

  const pending = (query: { data?: unknown; isError: boolean }, skipped: boolean) =>
    !skipped && query.data === undefined && !query.isError;

  const clinicParsed = clinicSettings.data ? enrollmentSettingsFrom(clinicSettings.data) : null;
  const accountParsed =
    !accountSkip && accountSettings.data ? enrollmentSettingsFrom(accountSettings.data) : null;
  const settings = resolveEnrollmentSettings(clinicParsed, accountParsed);
  const togglesKnown =
    clinicSkip || (!pending(clinicSettings, clinicSkip) && !pending(accountSettings, accountSkip));
  const multipleDiagnoses = accountMultipleDiagnoses(clinic);
  // The diagnoses list endpoint rejects clinics that are not multiple-diagnosis accounts
  // (401/500). Those clinics use the device-default code already on the patient.
  const diagnosisSkip = skip || !togglesKnown || !multipleDiagnoses;
  const insuranceSkip =
    skip || !togglesKnown || !isToggleOn(settings.enrollmentChecklist, 'insurance');
  const insuranceQuery = useGetPatientInsurancesQuery(patientId, { skip: insuranceSkip });
  const diagnosisQuery = useGetPatientDiagnosesInfiniteQuery(
    { patientId, limit: 20 },
    { skip: diagnosisSkip }
  );

  // Insurance and diagnosis failures are empty lists, same as the CPE page. A missing clinic
  // (patient loaded with no clinic, and the org lookup failed) is the only hard failure left.
  const clinicMissing = !clinicSkip && !pending(clinicQuery, clinicSkip) && !clinic;
  const error = !skip && (patientQuery.isError || (Boolean(patient) && !clinicId) || clinicMissing);

  // The account settings query only starts once the clinic (and its parent) is known.
  const loading =
    !skip &&
    !error &&
    (pending(patientQuery, skip) ||
      pending(clinicQuery, clinicSkip) ||
      pending(clinicSettings, clinicSkip) ||
      pending(accountSettings, accountSkip) ||
      pending(departmentQuery, clinicSkip) ||
      pending(insuranceQuery, insuranceSkip) ||
      pending(diagnosisQuery, diagnosisSkip));

  const evaluation = useMemo(() => {
    if (skip || loading || error || !patient || !clinic) return null;
    const hfMonitoring = resolveHfMonitoring(
      hfSchedulingFrom(clinicSettings.data),
      hfSchedulingFrom(accountSkip ? undefined : accountSettings.data)
    );
    const activeDevice = pickEnrollmentDevice(patient.matchDevices ?? []) ?? null;
    const device = activeDevice ?? pickInactiveEnrollmentDevice(patient.matchDevices ?? []);
    const primaryPolicy =
      sortPolicies(insuranceQuery.data ?? []).find(
        (policy) => policy.active && policy.hierarchy === 'primary'
      ) ?? null;
    const departments = departmentQuery.data?.items ?? [];
    const soleDepartment =
      departments.length === 1 ? { id: departments[0].id, name: departments[0].name } : null;
    const diagnoses = diagnosisFactsFromPages(diagnosisQuery.data?.pages);
    return evaluateEnrollmentChecklist(
      buildEnrollmentChecklistInputs({
        patient,
        clinic,
        clinicId,
        departments,
        soleDepartment,
        diagnosisCodes: diagnoses.codes,
        diagnosisHasPrimary: diagnosisSkip ? undefined : diagnoses.hasPrimary,
        device,
        activeDevice,
        primaryPolicy,
        enrollment: null,
        billingType: accountBillingType(clinic),
        ehrIntegrated: accountEhrIntegrated(clinic),
        hfMonitoring,
        settings,
        vendorName
      })
    );
  }, [
    skip,
    loading,
    error,
    patient,
    clinic,
    clinicId,
    accountSkip,
    clinicSettings.data,
    accountSettings.data,
    insuranceQuery.data,
    departmentQuery.data,
    diagnosisQuery.data,
    diagnosisSkip,
    settings
  ]);

  return { loading, error, evaluation };
}
